import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, throwError } from 'rxjs'
import { catchError, map, tap } from 'rxjs/operators'
import {
  AIAssignCsvToUser,
  AIDictionaryGenerationRequest,
} from 'src/app/core/ddc-admin-ai/ddc-admin-ai.component'
import { Backend } from '../../shared/enums/enums'
import { DdcLoggerService } from '../log/ddc-logger.service'

export interface AdminSession {
  isAuthenticated: boolean
  sessionToken: string | null
  expiresIn: number | null
  expiresAt: Date | null
}

export interface AdminDashboardData {
  users: {
    total: number
    active: number
    superusers: number
    verified: number
  }
  subscriptions: {
    plus_users: number
    pro_users: number
    free_tier_users: number
    total_subscribers: number
  }
  content: {
    words: number
    pairs: number
    dictionaries: number
    categories: number
    tags: number
  }
  recent_activity: {
    new_users_today: number
    new_words_today: number
    new_dictionaries_today: number
  }
}

export interface UserData {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  is_active: boolean
  is_staff: boolean
  is_superuser: boolean
  date_joined: string
  last_login: string | null
  has_profile_pic: boolean
  profile_pic_url: string | null
  bio: string
  country: string
  city: string
  nationality: string
  phone: string
  birth_date: string | null
  language_1: string
  language_2: string
  language_3: string
  created_at: string | null
  last_update: string | null
  subscription: string
  subscription_start: string | null
  subscription_end: string | null
}

export interface PaginationInfo {
  current_page: number
  total_pages: number
  total_users: number
  page_size: number
  has_next: boolean
  has_previous: boolean
  next_page: number | null
  previous_page: number | null
}

export interface UserManagementResponse {
  success: boolean
  users: UserData[]
  pagination: PaginationInfo
}

@Injectable({
  providedIn: 'root',
})
export class AdminPanelService {
  private adminSessionSubject = new BehaviorSubject<AdminSession>({
    isAuthenticated: false,
    sessionToken: null,
    expiresIn: null,
    expiresAt: null,
  })

  public adminSession$ = this.adminSessionSubject.asObservable()
  private sessionCheckInterval: any

  constructor(
    private http: HttpClient,
    private logger: DdcLoggerService,
  ) {
    this.loadSessionFromStorage()
    this.startSessionCheck()
  }

  /**
   * Get all AI-generated dictionaries (admin)
   */
  getAIGeneratedDictionaries(): Observable<any[]> {
    const currentSession = this.adminSessionSubject.value
    if (!currentSession.isAuthenticated || !currentSession.sessionToken) {
      return throwError('No valid admin session')
    }
    return this.http
      .post<any>(
        `${Backend.HOST_CONTAINER}${Backend.ADMIN_AI_GET_DICS}`,
        { session_token: currentSession.sessionToken },
        { withCredentials: true },
      )
      .pipe(
        map(response => {
          if (response.success && Array.isArray(response.dictionaries)) {
            return response.dictionaries
          }
          throw new Error(response.message || 'Failed to fetch AI-generated dictionaries')
        }),
        catchError(error => {
          this.logger.error('AdminPanelService | getAIGeneratedDictionaries | Error:', error)
          if (error.status === 401) {
            this.clearAdminSession()
          }
          return throwError(error)
        }),
      )
  }

  /**
   * Delete an AI-generated dictionary (admin)
   */
  deleteAIGeneratedDictionary(id: any): Observable<any> {
    const currentSession = this.adminSessionSubject.value
    if (!currentSession.isAuthenticated || !currentSession.sessionToken) {
      return throwError('No valid admin session')
    }
    return this.http
      .post<any>(
        `${Backend.HOST_CONTAINER}${Backend.ADMIN_AI_DELETE_DIC}`,
        { session_token: currentSession.sessionToken, id: id },
        { withCredentials: true },
      )
      .pipe(
        map(response => {
          if (response.success) {
            return response
          }
          throw new Error(response.message || 'Failed to delete AI-generated dictionary')
        }),
        catchError(error => {
          this.logger.error('AdminPanelService | deleteAIGeneratedDictionary | Error:', error)
          if (error.status === 401) {
            this.clearAdminSession()
          }
          return throwError(error)
        }),
      )
  }

  /**
   * Get available AI-generated CSV files for assignment (from backend)
   */
  getAvailableCsvFiles(): Observable<string[]> {
    const currentSession = this.adminSessionSubject.value
    if (!currentSession.isAuthenticated || !currentSession.sessionToken) {
      return throwError('No valid admin session')
    }
    return this.http
      .post<any>(
        `${Backend.HOST_CONTAINER}${Backend.ADMIN_AI_GET_CSV_FILES_LIST}`,
        { session_token: currentSession.sessionToken },
        { withCredentials: true },
      )
      .pipe(
        map(response => {
          if (response.success && Array.isArray(response.files)) {
            return response.files
          }
          throw new Error(response.message || 'Failed to fetch CSV file list')
        }),
        catchError(error => {
          this.logger.error('AdminPanelService | getAvailableCsvFiles | Error:', error)
          if (error.status === 401) {
            this.clearAdminSession()
          }
          return throwError(error)
        }),
      )
  }
  /**
   * Verify the 6-digit admin access code
   */
  verifyAdminCode(code: string): Observable<{
    success: boolean
    message: string
    session_token?: string
    expires_in?: number
  }> {
    this.logger.info('AdminPanelService | verifyAdminCode')

    return this.http
      .post<any>(
        `${Backend.HOST_CONTAINER}${Backend.ADMIN_VERIFY_CODE}`,
        {
          code: code,
        },
        { withCredentials: true },
      )
      .pipe(
        tap(response => {
          if (response.success && response.session_token) {
            this.setAdminSession(response.session_token, response.expires_in)
          }
        }),
        catchError(error => {
          this.logger.error('AdminPanelService | verifyAdminCode | Error:', error)
          return throwError(error)
        }),
      )
  }

  /**
   * Validate current admin session
   */
  validateSession(): Observable<boolean> {
    const currentSession = this.adminSessionSubject.value

    if (!currentSession.isAuthenticated || !currentSession.sessionToken) {
      return new Observable<boolean>(observer => {
        observer.next(false)
        observer.complete()
      })
    }

    return this.http
      .post<any>(
        `${Backend.HOST_CONTAINER}${Backend.ADMIN_VALIDATE_SESSION}`,
        {
          session_token: currentSession.sessionToken,
        },
        { withCredentials: true },
      )
      .pipe(
        map(response => {
          if (response.success) {
            // Update expiration time
            this.updateSessionExpiration(response.expires_in)
            return true
          }
          this.clearAdminSession()
          return false
        }),
        catchError(error => {
          this.logger.error('AdminPanelService | validateSession | Error:', error)
          this.clearAdminSession()
          return new Observable<boolean>(observer => {
            observer.next(false)
            observer.complete()
          })
        }),
      )
  }

  /**
   * Get admin dashboard data
   */
  getDashboardData(): Observable<AdminDashboardData> {
    const currentSession = this.adminSessionSubject.value

    if (!currentSession.isAuthenticated || !currentSession.sessionToken) {
      return throwError('No valid admin session')
    }

    return this.http
      .post<any>(
        `${Backend.HOST_CONTAINER}${Backend.ADMIN_DASHBOARD}`,
        {
          session_token: currentSession.sessionToken,
        },
        { withCredentials: true },
      )
      .pipe(
        map(response => {
          if (response.success) {
            return response.data
          }
          throw new Error(response.message || 'Failed to get dashboard data')
        }),
        catchError(error => {
          this.logger.error('AdminPanelService | getDashboardData | Error:', error)
          if (error.status === 401) {
            this.clearAdminSession()
          }
          return throwError(error)
        }),
      )
  }

  /**
   * Get users data with pagination
   */
  getUsersData(page: number = 1, pageSize: number = 10): Observable<UserManagementResponse> {
    const currentSession = this.adminSessionSubject.value

    if (!currentSession.isAuthenticated || !currentSession.sessionToken) {
      return throwError('No valid admin session')
    }

    return this.http
      .post<UserManagementResponse>(
        `${Backend.HOST_CONTAINER}${Backend.ADMIN_USERS}`,
        {
          session_token: currentSession.sessionToken,
          page: page,
          page_size: pageSize,
        },
        { withCredentials: true },
      )
      .pipe(
        map(response => {
          if (response.success) {
            return response
          }
          throw new Error(response.success === false ? 'Failed to get users data' : 'Unknown error')
        }),
        catchError(error => {
          this.logger.error('AdminPanelService | getUsersData | Error:', error)
          if (error.status === 401) {
            this.clearAdminSession()
          }
          return throwError(error)
        }),
      )
  }

  /**
   * Generate AI dictionary request
   */
  generateAIDictionary(request: AIDictionaryGenerationRequest): Observable<any> {
    const currentSession = this.adminSessionSubject.value

    if (!currentSession.isAuthenticated || !currentSession.sessionToken) {
      return throwError('No valid admin session')
    }

    return this.http
      .post<any>(
        `${Backend.HOST_CONTAINER}${Backend.ADMIN_AI_GENERATE}`,
        {
          session_token: currentSession.sessionToken,
          admin_req: { ...request },
        },
        { withCredentials: true },
      )
      .pipe(
        map(response => {
          if (response.success) {
            return response
          }
          throw new Error(response.message || 'Failed to generate AI dictionary')
        }),
        catchError(error => {
          this.logger.error('AdminPanelService | generateAIDictionary | Error:', error)
          if (error.status === 401) {
            this.clearAdminSession()
          }
          return throwError(error)
        }),
      )
  }

  /**
   * update AI generated index by running script
   */
  updateAIGeneratedIndex(): Observable<any> {
    const currentSession = this.adminSessionSubject.value

    if (!currentSession.isAuthenticated || !currentSession.sessionToken) {
      return throwError('No valid admin session')
    }

    return this.http
      .post<any>(
        `${Backend.HOST_CONTAINER}${Backend.ADMIN_AI_UPDATE_INDEX}`,
        {
          session_token: currentSession.sessionToken,
        },
        { withCredentials: true },
      )
      .pipe(
        map(response => {
          if (response.success) {
            return response
          }
          throw new Error(response.message || 'Failed to update AI generated index')
        }),
        catchError(error => {
          this.logger.error('AdminPanelService | updateAIGeneratedIndex | Error:', error)
          if (error.status === 401) {
            this.clearAdminSession()
          }
          return throwError(error)
        }),
      )
  }

  assignCsvToUser(payload: AIAssignCsvToUser): Observable<any> {
    const currentSession = this.adminSessionSubject.value

    if (!currentSession.isAuthenticated || !currentSession.sessionToken) {
      return throwError('No valid admin session')
    }

    const formData = new FormData()
    formData.append('session_token', currentSession.sessionToken)
    formData.append('user_id', payload.userId.toString())
    formData.append('csv_file', payload.csvFile)
    formData.append('privacy', payload.privacy)

    return this.http
      .post<any>(`${Backend.HOST_CONTAINER}${Backend.ADMIN_AI_ASSIGN_TO_USER}`, formData, {
        withCredentials: true,
      })
      .pipe(
        map(response => {
          if (response.success) {
            return response
          }
          throw new Error(response.message || 'Failed to assign CSV to user')
        }),
        catchError(error => {
          this.logger.error('AdminPanelService | assignCsvToUser | Error:', error)
          if (error.status === 401) {
            this.clearAdminSession()
          }
          return throwError(error)
        }),
      )
  }

  /**
   * Logout from admin session
   */
  logoutAdmin(): Observable<boolean> {
    const currentSession = this.adminSessionSubject.value

    if (!currentSession.sessionToken) {
      this.clearAdminSession()
      return new Observable<boolean>(observer => {
        observer.next(true)
        observer.complete()
      })
    }

    return this.http
      .post<any>(
        `${Backend.HOST_CONTAINER}${Backend.ADMIN_LOGOUT}`,
        {
          session_token: currentSession.sessionToken,
        },
        { withCredentials: true },
      )
      .pipe(
        map(response => {
          this.clearAdminSession()
          return response.success
        }),
        catchError(error => {
          this.logger.error('AdminPanelService | logoutAdmin | Error:', error)
          this.clearAdminSession()
          return new Observable<boolean>(observer => {
            observer.next(true)
            observer.complete()
          })
        }),
      )
  }

  /**
   * Check if user has admin session
   */
  isAdminAuthenticated(): boolean {
    const session = this.adminSessionSubject.value
    return session.isAuthenticated && !!session.sessionToken && this.isSessionValid()
  }

  /**
   * Get current session token
   */
  getSessionToken(): string | null {
    return this.adminSessionSubject.value.sessionToken
  }

  private setAdminSession(sessionToken: string, expiresIn: number): void {
    const expiresAt = new Date(Date.now() + expiresIn * 1000)

    const session: AdminSession = {
      isAuthenticated: true,
      sessionToken,
      expiresIn,
      expiresAt,
    }

    this.adminSessionSubject.next(session)
    this.saveSessionToStorage(session)
    this.logger.info('AdminPanelService | Admin session set successfully')
  }

  private updateSessionExpiration(expiresIn: number): void {
    const currentSession = this.adminSessionSubject.value
    if (currentSession.isAuthenticated) {
      const updatedSession: AdminSession = {
        ...currentSession,
        expiresIn,
        expiresAt: new Date(Date.now() + expiresIn * 1000),
      }
      this.adminSessionSubject.next(updatedSession)
      this.saveSessionToStorage(updatedSession)
    }
  }

  private clearAdminSession(): void {
    const emptySession: AdminSession = {
      isAuthenticated: false,
      sessionToken: null,
      expiresIn: null,
      expiresAt: null,
    }

    this.adminSessionSubject.next(emptySession)
    this.removeSessionFromStorage()
    this.logger.info('AdminPanelService | Admin session cleared')
  }

  private isSessionValid(): boolean {
    const session = this.adminSessionSubject.value
    if (!session.expiresAt) return false

    return new Date() < session.expiresAt
  }

  private saveSessionToStorage(session: AdminSession): void {
    try {
      localStorage.setItem(
        'ddc_admin_session',
        JSON.stringify({
          sessionToken: session.sessionToken,
          expiresIn: session.expiresIn,
          expiresAt: session.expiresAt?.toISOString(),
        }),
      )
    } catch (error) {
      this.logger.error('AdminPanelService | Error saving session to storage:', error)
    }
  }

  private loadSessionFromStorage(): void {
    try {
      const storedSession = localStorage.getItem('ddc_admin_session')
      if (storedSession) {
        const parsedSession = JSON.parse(storedSession)
        const expiresAt = new Date(parsedSession.expiresAt)

        if (expiresAt > new Date()) {
          const session: AdminSession = {
            isAuthenticated: true,
            sessionToken: parsedSession.sessionToken,
            expiresIn: parsedSession.expiresIn,
            expiresAt,
          }
          this.adminSessionSubject.next(session)
        } else {
          this.removeSessionFromStorage()
        }
      }
    } catch (error) {
      this.logger.error('AdminPanelService | Error loading session from storage:', error)
      this.removeSessionFromStorage()
    }
  }

  private removeSessionFromStorage(): void {
    try {
      localStorage.removeItem('ddc_admin_session')
    } catch (error) {
      this.logger.error('AdminPanelService | Error removing session from storage:', error)
    }
  }

  private startSessionCheck(): void {
    // Check session validity every 5 minutes
    this.sessionCheckInterval = setInterval(
      () => {
        if (this.adminSessionSubject.value.isAuthenticated) {
          this.validateSession().subscribe()
        }
      },
      5 * 60 * 1000,
    )
  }

  ngOnDestroy(): void {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval)
    }
  }
}
